
import React, { useState } from 'react';
import { Coupon, DiscountType, CouponRedemption } from '../types';
import { CURRENCIES } from '../constants';

interface CouponTableProps {
  coupons: Coupon[];
  onCopy: (code: string) => void;
  copyFeedback: string | null;
}

const CouponTable: React.FC<CouponTableProps> = ({ coupons, onCopy, copyFeedback }) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Expired': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getCurrencySymbol = (code: string) => {
    return CURRENCIES.find(c => c.code === code)?.symbol || '₹';
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Discount</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Summary</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Duration</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Usage</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {coupons.map((coupon) => (
              <React.Fragment key={coupon.id}>
                <tr className={`group hover:bg-slate-50/50 transition-colors cursor-pointer ${expandedRow === coupon.id ? 'bg-indigo-50/30' : ''}`} onClick={() => toggleExpand(coupon.id)}>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-black text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-100 text-sm">{coupon.code}</span>
                      <button onClick={(e) => { e.stopPropagation(); onCopy(coupon.code); }} className="text-slate-300 hover:text-indigo-600 transition-colors">
                        {copyFeedback === coupon.code ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polyline points="20 6 9 17 4 12"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>}
                      </button>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-slate-900">
                        {coupon.discountType === DiscountType.PERCENTAGE 
                          ? `${coupon.value}% Off` 
                          : `${getCurrencySymbol(coupon.currency)}${coupon.value} Off`}
                      </span>
                      <span className="text-[10px] text-slate-400 uppercase font-black tracking-tighter">
                        {coupon.minPurchase ? `Min ${getCurrencySymbol(coupon.currency)}${coupon.minPurchase}` : 'No Minimum'} • {coupon.limitPerCustomer ? '1/User' : 'Unlimited/User'}
                      </span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(coupon.status)}`}>{coupon.status}</span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-600">{new Date(coupon.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                        {coupon.expiryDate ? `To ${new Date(coupon.expiryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'Always Active'}
                      </span>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex flex-col gap-1.5 w-32">
                      <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase"><span>{coupon.currentRedemptions}</span><span>{coupon.maxRedemptions || '∞'}</span></div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-1000 ${coupon.status === 'Expired' ? 'bg-slate-300' : 'bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.4)]'}`} style={{ width: `${coupon.maxRedemptions ? Math.min(100, (coupon.currentRedemptions / coupon.maxRedemptions) * 100) : 100}%` }}></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex justify-end gap-2">
                      <button className={`p-2 rounded-lg transition-colors ${expandedRow === coupon.id ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:text-indigo-600 hover:bg-slate-100'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-300 ${expandedRow === coupon.id ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === coupon.id && (
                  <tr className="bg-slate-50/50 animate-in slide-in-from-top-2 duration-300">
                    <td colSpan={6} className="px-12 py-8">
                      <div className="space-y-6">
                        <div className="flex items-center gap-3">
                           <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse"></div>
                           <h4 className="text-[11px] font-black text-slate-900 uppercase tracking-[0.2em]">Recent Redemptions Audit</h4>
                        </div>
                        
                        {coupon.redemptions && coupon.redemptions.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {coupon.redemptions.map((redemption, idx) => (
                              <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-4 flex justify-between items-center shadow-sm">
                                <div>
                                  <p className="text-xs font-black text-slate-900">{redemption.customerName}</p>
                                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">{new Date(redemption.redeemedAt).toLocaleString()}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs font-black text-emerald-600">-{getCurrencySymbol(coupon.currency)}{redemption.savedAmount.toLocaleString()}</p>
                                  <p className="text-[9px] text-slate-400 font-black uppercase">Saved</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-10 text-center bg-white border border-dashed border-slate-200 rounded-3xl">
                             <svg className="w-10 h-10 mx-auto text-slate-200 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                             <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No redemptions logged for this code yet.</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CouponTable;
